import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Edit,
  Building2,
  Phone,
  Mail,
  Plus,
  Trash2,
  Clock,
  DollarSign,
  Calendar,
  User,
  FileText,
  CheckCircle,
  History,
  Save,
} from 'lucide-react';
import { getJob, updateJob, updateJobStatus, deleteJob } from '../lib/api';
import { formatCurrency, formatDate, formatRelativeTime } from '../lib/utils';

const JOB_STATUSES = ['draft', 'quoted', 'approved', 'in_progress', 'completed', 'invoiced', 'paid', 'cancelled'];

const JOB_STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  quoted: 'bg-blue-100 text-blue-700',
  approved: 'bg-purple-100 text-purple-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  invoiced: 'bg-orange-100 text-orange-700',
  paid: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

const STATUS_ICONS = {
  draft: FileText,
  quoted: DollarSign,
  approved: CheckCircle,
  in_progress: Clock,
  completed: CheckCircle,
  invoiced: FileText,
  paid: DollarSign,
  cancelled: Trash2,
};

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [history, setHistory] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [statusNote, setStatusNote] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [saving, setSaving] = useState(false);

  const loadJob = async () => {
    try {
      setLoading(true);
      const response = await getJob(jobId);
      setJob(response.data.job);
      setCustomer(response.data.customer);
      setHistory(response.data.history || []);
      setEditForm(response.data.job);
    } catch (error) {
      toast.error('Failed to load job');
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJob();
  }, [jobId]);

  const calculateTotals = (items, gstRate) => {
    const subtotal = items.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
    }, 0);
    const gst = subtotal * ((gstRate || 10) / 100);
    return { subtotal, gst, total: subtotal + gst };
  };

  const handleAddWorkItem = () => {
    setEditForm({
      ...editForm,
      work_items: [...(editForm.work_items || []), { description: '', quantity: 1, unit: 'each', unit_price: 0 }],
    });
  };

  const handleRemoveWorkItem = (index) => {
    if (editForm.work_items.length === 1) return;
    setEditForm({
      ...editForm,
      work_items: editForm.work_items.filter((_, i) => i !== index),
    });
  };

  const handleWorkItemChange = (index, field, value) => {
    const items = [...editForm.work_items];
    items[index][field] = value;
    setEditForm({ ...editForm, work_items: items });
  };

  const handleSaveJob = async () => {
    setSaving(true);
    try {
      await updateJob(jobId, editForm);
      toast.success('Job updated successfully');
      setIsEditing(false);
      loadJob();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update job');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus) return;
    setSaving(true);
    try {
      await updateJobStatus(jobId, newStatus, statusNote);
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      setShowStatusDialog(false);
      setNewStatus('');
      setStatusNote('');
      loadJob();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteJob = async () => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    try {
      await deleteJob(jobId);
      toast.success('Job deleted');
      navigate(`/customers/${customer.customer_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete job');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card><CardContent className="p-6"><Skeleton className="h-48" /></CardContent></Card>
      </div>
    );
  }

  if (!job) return null;

  const totals = isEditing 
    ? calculateTotals(editForm.work_items || [], editForm.gst_rate)
    : { subtotal: job.subtotal, gst: job.gst_amount, total: job.total_with_gst };

  const StatusIcon = STATUS_ICONS[job.status] || FileText;

  return (
    <div className="space-y-6" data-testid="job-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(`/customers/${customer?.customer_id}`)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Customer
        </Button>
      </div>

      {/* Job Header Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm font-mono text-slate-400">{job.job_number}</span>
                <Badge className={JOB_STATUS_COLORS[job.status]}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {job.status.replace('_', ' ')}
                </Badge>
              </div>
              <h1 className="text-2xl font-bold text-slate-900">{isEditing ? editForm.title : job.title}</h1>
              {(isEditing ? editForm.description : job.description) && (
                <p className="text-slate-600 mt-2">{isEditing ? editForm.description : job.description}</p>
              )}
              {customer && (
                <div className="flex items-center gap-4 mt-4 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <User className="w-4 h-4" />
                    <span className="font-medium">{customer.name}</span>
                    {customer.company && <span className="text-slate-400">({customer.company})</span>}
                  </div>
                  {customer.phone && (
                    <a href={`tel:${customer.phone}`} className="flex items-center gap-1 text-teal-600 hover:underline">
                      <Phone className="w-3 h-3" />
                      {customer.phone}
                    </a>
                  )}
                  {customer.email && (
                    <a href={`mailto:${customer.email}`} className="flex items-center gap-1 text-teal-600 hover:underline">
                      <Mail className="w-3 h-3" />
                      {customer.email}
                    </a>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setShowStatusDialog(true)}>
                Update Status
              </Button>
              {isEditing ? (
                <>
                  <Button variant="outline" onClick={() => { setIsEditing(false); setEditForm(job); }}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveJob} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setIsEditing(true)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  {!['invoiced', 'paid'].includes(job.status) && (
                    <Button variant="destructive" onClick={handleDeleteJob}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Work Items */}
        <div className="md:col-span-2 space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Work Items</CardTitle>
              {isEditing && (
                <Button size="sm" variant="outline" onClick={handleAddWorkItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-12 gap-2 text-sm font-medium text-slate-500 px-3">
                    <div className="col-span-5">Description</div>
                    <div className="col-span-2">Qty</div>
                    <div className="col-span-2">Unit</div>
                    <div className="col-span-2">Price</div>
                    <div className="col-span-1"></div>
                  </div>
                  {editForm.work_items?.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-start p-3 bg-slate-50 rounded-lg">
                      <div className="col-span-5">
                        <Input
                          placeholder="Description"
                          value={item.description}
                          onChange={(e) => handleWorkItemChange(index, 'description', e.target.value)}
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleWorkItemChange(index, 'quantity', e.target.value)}
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          value={item.unit}
                          onChange={(e) => handleWorkItemChange(index, 'unit', e.target.value)}
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => handleWorkItemChange(index, 'unit_price', e.target.value)}
                        />
                      </div>
                      <div className="col-span-1 flex items-center justify-center">
                        {editForm.work_items.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveWorkItem(index)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-sm text-slate-500 border-b">
                        <th className="text-left py-3 px-2">#</th>
                        <th className="text-left py-3 px-2">Description</th>
                        <th className="text-right py-3 px-2">Qty</th>
                        <th className="text-left py-3 px-2">Unit</th>
                        <th className="text-right py-3 px-2">Unit Price</th>
                        <th className="text-right py-3 px-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {job.work_items?.map((item, index) => (
                        <tr key={index} className="border-b last:border-0">
                          <td className="py-3 px-2 text-slate-500">{item.line_number}</td>
                          <td className="py-3 px-2">{item.description}</td>
                          <td className="py-3 px-2 text-right">{item.quantity}</td>
                          <td className="py-3 px-2">{item.unit}</td>
                          <td className="py-3 px-2 text-right">{formatCurrency(item.unit_price)}</td>
                          <td className="py-3 px-2 text-right font-medium">{formatCurrency(item.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Status History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No status changes recorded</p>
              ) : (
                <div className="space-y-4">
                  {history.map((entry, index) => (
                    <div key={entry.history_id} className="flex items-start gap-4">
                      <div className={`w-3 h-3 mt-1.5 rounded-full ${index === 0 ? 'bg-teal-500' : 'bg-slate-300'}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {entry.old_status && (
                            <>
                              <Badge variant="outline" className="text-xs">{entry.old_status}</Badge>
                              <span className="text-slate-400">→</span>
                            </>
                          )}
                          <Badge className={JOB_STATUS_COLORS[entry.new_status]}>{entry.new_status}</Badge>
                        </div>
                        {entry.notes && <p className="text-sm text-slate-600 mt-1">{entry.notes}</p>}
                        <p className="text-xs text-slate-400 mt-1">{formatRelativeTime(entry.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Totals Card */}
          <Card>
            <CardHeader>
              <CardTitle>Job Total</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Subtotal:</span>
                <span className="font-medium">{formatCurrency(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">GST ({isEditing ? editForm.gst_rate : job.gst_rate}%):</span>
                <span className="font-medium">{formatCurrency(totals.gst)}</span>
              </div>
              <div className="flex justify-between text-xl font-bold border-t pt-3">
                <span>Total:</span>
                <span className="text-teal-600">{formatCurrency(totals.total)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Job Details */}
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isEditing ? (
                <>
                  <div className="grid gap-2">
                    <Label>Title</Label>
                    <Input 
                      value={editForm.title} 
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} 
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Description</Label>
                    <Textarea 
                      value={editForm.description || ''} 
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} 
                      rows={3}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>GST Rate (%)</Label>
                    <Input 
                      type="number" 
                      value={editForm.gst_rate} 
                      onChange={(e) => setEditForm({ ...editForm, gst_rate: parseFloat(e.target.value) || 0 })} 
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Due Date</Label>
                    <Input 
                      type="date" 
                      value={editForm.due_date?.split('T')[0] || ''} 
                      onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} 
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Notes</Label>
                    <Textarea 
                      value={editForm.notes || ''} 
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} 
                      rows={3}
                    />
                  </div>
                </>
              ) : (
                <>
                  {job.due_date && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-500">Due:</span>
                      <span className="font-medium">{formatDate(job.due_date)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Created:</span>
                    <span>{formatDate(job.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Updated:</span>
                    <span>{formatRelativeTime(job.updated_at)}</span>
                  </div>
                  {job.notes && (
                    <div className="pt-2 border-t">
                      <p className="text-sm text-slate-500 mb-1">Notes:</p>
                      <p className="text-sm text-slate-700">{job.notes}</p>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Update Status Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Job Status</DialogTitle>
            <DialogDescription>
              Change the status of job {job.job_number}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Current Status</Label>
              <Badge className={`${JOB_STATUS_COLORS[job.status]} w-fit`}>
                {job.status.replace('_', ' ')}
              </Badge>
            </div>
            <div className="grid gap-2">
              <Label>New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent>
                  {JOB_STATUSES.map((status) => (
                    <SelectItem key={status} value={status} disabled={status === job.status}>
                      {status.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Notes (optional)</Label>
              <Textarea 
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                placeholder="Add a note about this status change..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleStatusChange} 
              disabled={!newStatus || saving}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="update-status-btn"
            >
              {saving ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
